import { Button } from "@heroui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { ApiError, apiRequest } from "@/shared/api/client";

type AdminUser = {
  id: string;
  name: string | null;
  email: string;
  tenantTypes: string[];
  lastLoginAt: string | null;
};

type UserSuggestion = Pick<AdminUser, "id" | "name" | "email">;

export function AdminUsersPage() {
  const queryClient = useQueryClient();
  const [transferFor, setTransferFor] = useState<AdminUser>();
  const [emailQuery, setEmailQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState<UserSuggestion>();
  const [message, setMessage] = useState<string>();
  const usersQuery = useQuery({
    queryKey: ["admin", "users"],
    queryFn: () => apiRequest<{ data: AdminUser[] }>("/api/admin/users"),
  });
  const suggestionsQuery = useQuery({
    queryKey: ["admin", "user-search", emailQuery, transferFor?.id],
    queryFn: () =>
      apiRequest<{ data: UserSuggestion[] }>(
        `/api/admin/users/search?email=${encodeURIComponent(emailQuery)}&excludeUserId=${transferFor!.id}`,
      ),
    enabled: Boolean(transferFor && emailQuery.trim().length >= 2),
  });
  const deletion = useMutation({
    mutationFn: ({ userId, transferOwnerUserId }: { userId: string; transferOwnerUserId?: string }) =>
      apiRequest<{ data: { deletedUserId: string } }>(`/api/admin/users/${userId}`, {
        method: "DELETE",
        ...(transferOwnerUserId
          ? { body: JSON.stringify({ transferOwnerUserId }) }
          : {}),
      }),
    onSuccess: async () => {
      closeTransfer();
      setMessage("Account deleted successfully.");
      await queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
    onError: (error, variables) => {
      if (error instanceof ApiError && error.code === "OWNERSHIP_TRANSFER_REQUIRED") {
        const user = usersQuery.data?.data.find(({ id }) => id === variables.userId);
        if (user) setTransferFor(user);
        return;
      }
      setMessage(error instanceof Error ? error.message : "Unable to delete account.");
    },
  });

  function tryDelete(user: AdminUser) {
    setMessage(undefined);
    if (window.confirm(`Delete ${user.email} and their personal workspace?`))
      deletion.mutate({ userId: user.id });
  }

  function closeTransfer() {
    setTransferFor(undefined);
    setEmailQuery("");
    setSelectedUser(undefined);
  }

  return (
    <main className="mx-auto max-w-6xl px-6 py-10">
      <h1 className="text-3xl font-semibold">Account administration</h1>
      <p className="mt-2 text-muted">Delete test accounts and safely transfer final workspace ownership.</p>
      {message && <p className="mt-4 rounded-xl bg-soft p-3" role="status">{message}</p>}
      <div className="mt-8 overflow-x-auto rounded-2xl border border-separator bg-background">
        <table className="w-full border-collapse text-left">
          <thead className="border-b border-separator bg-soft text-sm">
            <tr>
              <th className="p-4">User name</th>
              <th className="p-4">User email</th>
              <th className="p-4">Tenant type</th>
              <th className="p-4">Last login</th>
              <th className="p-4">Action</th>
            </tr>
          </thead>
          <tbody>
            {usersQuery.data?.data.map((user) => (
              <tr className="border-b border-separator last:border-0" key={user.id}>
                <td className="p-4">{user.name ?? "—"}</td>
                <td className="p-4">{user.email}</td>
                <td className="p-4">{user.tenantTypes.join(", ") || "—"}</td>
                <td className="p-4">{user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : "Never"}</td>
                <td className="p-4">
                  <Button isDisabled={deletion.isPending} variant="danger" onPress={() => tryDelete(user)}>
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {usersQuery.isLoading && <p className="p-6">Loading accounts…</p>}
        {usersQuery.isError && <p className="p-6 text-danger">Unable to load accounts.</p>}
      </div>

      {transferFor && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/45 p-5">
          <section aria-labelledby="transfer-title" aria-modal="true" className="w-full max-w-lg rounded-3xl bg-background p-8 shadow-2xl" role="dialog">
            <h2 className="text-2xl font-semibold" id="transfer-title">Transfer ownership</h2>
            <p className="mt-2 text-muted">{transferFor.email} is the final Owner of one or more organization workspaces. Select another active user before deletion.</p>
            <label className="mt-6 block text-sm font-semibold" htmlFor="transfer-email">Find user by email</label>
            <input
              autoComplete="off"
              className="mt-2 w-full rounded-xl border border-separator px-4 py-3"
              id="transfer-email"
              placeholder="Start typing an email"
              value={emailQuery}
              onChange={(event) => {
                setEmailQuery(event.target.value);
                setSelectedUser(undefined);
              }}
            />
            {suggestionsQuery.data?.data.length ? (
              <ul className="mt-2 overflow-hidden rounded-xl border border-separator" role="listbox">
                {suggestionsQuery.data.data.map((user) => (
                  <li key={user.id}>
                    <button className="w-full px-4 py-3 text-left hover:bg-soft" type="button" onClick={() => {
                      setSelectedUser(user);
                      setEmailQuery(user.email);
                    }}>
                      <span className="font-medium">{user.email}</span>{user.name ? ` — ${user.name}` : ""}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            <div className="mt-7 flex justify-end gap-3">
              <Button variant="tertiary" onPress={closeTransfer}>Cancel</Button>
              <Button
                isDisabled={!selectedUser || deletion.isPending}
                variant="danger"
                onPress={() => selectedUser && deletion.mutate({ userId: transferFor.id, transferOwnerUserId: selectedUser.id })}
              >
                Transfer and delete
              </Button>
            </div>
          </section>
        </div>
      )}
    </main>
  );
}
